// page-actions/export.ts — Export XLSX / JSON de la liste #actions.
//
// Quatrième tranche extraite de `renderActionsPage` (point 16, 2026-09-08).
// La page garde ce qu'elle seule connaît : les actions filtrées, la sélection
// et l'état des filtres. Ici : la résolution des libellés i18n d'entête
// (`exportFilterLabels`, pure) et le flot d'export (dialog de périmètre quand
// des actions sont cochées, assemblage du payload, téléchargement, toast).
// Les décisions pures d'assemblage restent dans domain/actions-export.
//
// Globaux ambiants : t, toast, api, currentUserId (core-globals.d.ts).

import {
  selectExportActionIds,
  buildExportFilters,
  buildExportPayload,
  type ExportFilterLabels,
} from '../../domain/actions-export.js';
import type { ActionsFilterState } from '../../domain/actions-filter.js';
import type { ActionsCommittee } from './types.js';
import { confirmExportScope } from './render-page-fragments.js';

export type ExportFormat = 'xlsx' | 'json';

/** L'export n'a besoin que du libellé du comité filtré. */
export type ExportCommittee = Pick<ActionsCommittee, 'code' | 'name'>;

/** Libellé du filtre « priorité minimale » : échelle inversée, 0 = Très haute. */
export function priorityFilterLabel(val: number | string | null | undefined): string {
  if (val === '' || val == null) return t('pageActions.priority.all');
  const key = String(val);
  if (key === '0') return t('pageActions.priority.0only');
  if (key === '1') return t('pageActions.priority.1plus');
  if (key === '2') return t('pageActions.priority.2plus');
  if (key === '3') return t('pageActions.priority.3plus');
  return t('pageActions.priority.all');
}

/**
 * Libellés d'entête de l'export, résolus depuis l'état des filtres. Vides
 * quand le filtre est à sa valeur « tout » : le backend n'affiche que ce qui
 * restreint réellement la liste.
 */
export function exportFilterLabels(
  filters: ActionsFilterState,
  committeeMap: Record<number, ExportCommittee>,
): ExportFilterLabels {
  let monRole = '';
  if (!filters.fAll) {
    const parts: string[] = [];
    if (filters.fPerformer) parts.push(t('pageActions.export.roleR'));
    if (filters.fApprover) parts.push(t('pageActions.export.roleA'));
    if (filters.fConsulted) parts.push(t('pageActions.export.roleC'));
    if (filters.fInformed) parts.push(t('pageActions.export.roleI'));
    monRole = parts.join(', ');
  }

  let committeeLabel = '';
  if (filters.fCommittee === 'none') {
    committeeLabel = t('pageActions.export.committeeNone');
  } else if (filters.fCommittee && filters.fCommittee !== 'all') {
    const c = committeeMap[parseInt(filters.fCommittee)];
    committeeLabel = c ? `${c.code} — ${c.name}` : '';
  }

  const prio = priorityFilterLabel(filters.fPriorityMin);
  const priorityMinLabel = prio === t('pageActions.priority.all') ? '' : prio;

  const scopeLabel = filters.fAll
    ? t('pageActions.export.scopeAll')
    : t('pageActions.export.scopeMine');

  return { monRole, scopeLabel, committeeLabel, priorityMinLabel };
}

export interface ActionsExportContext<A extends { id?: number }> {
  /** Sélection courante de la liste (lue au moment du clic). */
  selectedIds: ReadonlySet<number>;
  /** Actions après application des filtres courants. */
  getFiltered: () => A[];
  getFilters: () => ActionsFilterState;
  committeeMap: Record<number, ExportCommittee>;
}

export interface ActionsExport {
  /** Point d'entrée des options du menu Export. */
  onExportClick: (fmt: ExportFormat) => Promise<void>;
}

export function createActionsExport<A extends { id?: number }>(
  ctx: ActionsExportContext<A>,
): ActionsExport {
  async function exportActions(fmt: ExportFormat, idsOverride?: number[]): Promise<void> {
    const filtered = ctx.getFiltered();
    if (!filtered.length) {
      toast(t('pageActions.export.empty'), 'error');
      return;
    }
    // Les ids cochés sont intersectés avec le filtre courant : un id peut
    // rester sélectionné après un changement de filtre.
    const allowedIds = selectExportActionIds(
      filtered.map((a) => a.id),
      idsOverride,
    );
    if (!allowedIds.length) {
      toast(t('pageActions.export.empty'), 'error');
      return;
    }
    const filters = ctx.getFilters();
    const payload = buildExportPayload({
      fmt,
      actionIds: allowedIds,
      filters: buildExportFilters(filters, exportFilterLabels(filters, ctx.committeeMap)),
      userId: currentUserId(),
      exportDt: new Date().toISOString(),
    });
    try {
      const blob = (await api.exportActions(payload)) as Blob;
      api.triggerBlobDownload(
        blob,
        `actions_export_${new Date().toISOString().slice(0, 10)}.${fmt}`,
      );
      toast(t('pageActions.export.toast', { fmt: fmt.toUpperCase(), n: allowedIds.length }));
    } catch (err: unknown) {
      const m = err instanceof Error ? err.message : t('common.error');
      toast(m, 'error');
    }
  }

  // Avec des actions cochées, une dialog demande le périmètre : cochées
  // seulement, toutes les filtrées, ou annuler.
  async function onExportClick(fmt: ExportFormat): Promise<void> {
    if (ctx.selectedIds.size === 0) {
      await exportActions(fmt);
      return;
    }
    const choice = await confirmExportScope(ctx.selectedIds.size);
    if (choice === 'cancel') return;
    await exportActions(fmt, choice === 'selected' ? Array.from(ctx.selectedIds) : undefined);
  }

  return { onExportClick };
}
